import { AxiosResponse } from "axios";
import { RemoteExecutionServiceImpl, RemotePOSTCallInputObject, ServerResponse } from "../../../remoteExecution/RemoteExecutionServiceImpl";
import { IPAddressDetailsDTO, SalesTrackerExitReqDTO, SalesTrackerHotIntakeReqDTO, WebBrowserClientSalesTrackerReqDTO, WebBrowserClientSalesTrackerResDTO } from "../sales-related-data/SalesTrackerHotIntakeInterfaces";
import { GetAppLoadCount, getOrCreateWorkySubSessionIdToSessionStorage, IncrementAppLoadCount, retrieveLocalStorageAttributes, saveWorkySessionIdToLocalStorage } from "../../../common/CommonUtilsMethods";
import { isBotOrCrawler } from "../sales-related-services/SalesTrackerServices";

const backendApiBasePathLocal = process.env.REACT_APP_BACK_END_SERVER_BASE_PATH_LOCAL;
const remoteCallObject = RemoteExecutionServiceImpl();




/**
 * Logs a sales-related user interaction/event to the backend telemetry system
 * @param eventName The name of the event being tracked (e.g., "page_view", "button_click")
 * @param eventData Additional data about the event to include in tracking
 * @returns The unique tracking ID if successful, null otherwise
 */
const TrackSalesEvent = async (
  {eventName, eventData, ipAddressDetails }
  :{eventName?: string, eventData?: Record<string, string>,
    ipAddressDetails?: IPAddressDetailsDTO
  }
): Promise<string | null> => {
  
  try {        
    if(!eventData){eventData={}}

    // Get current count from sessionStorage
    const currentCount = GetAppLoadCount();
    if (currentCount >= 25) {
        console.log("max reload count acheived will not make more sales calls ", currentCount)
        return "";
    }
    
    // Use your existing helper method to get all localStorage data
    const localStorageData = retrieveLocalStorageAttributes();
    
    // Get the session ID from localStorage data or create a new one
    const subSussionId = getOrCreateWorkySubSessionIdToSessionStorage();
    const workySessionId = saveWorkySessionIdToLocalStorage();    
    
    // Parse UTM parameters from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams: Record<string, string> = {};
    
    // Collect UTM parameters if present
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utmParams[param] = value;
      }
    });
    
    // Build the event data object with the event name and passed data
    const combinedEventData = {
      event_name: eventName,
      event_timestamp: Date.now().toString(),
      ...eventData,
      ...utmParams
    };
    
    // Build the payload using the structure expected by the backend
    const payload: WebBrowserClientSalesTrackerReqDTO = {
      timestamp: Date.now(),
      currentUrl: window.location.href,
      userAgent: navigator.userAgent,
      trackingReason: eventName,
      screenWidth: window.innerWidth.toString(),
      screenHeight: window.innerHeight.toString(),
      referrerUrl: document.referrer || "",
      subSessionId: subSussionId,      
      workySessionId: workySessionId,
      ipAddressDetails: ipAddressDetails,
      additionalData: {
        ...combinedEventData,
        // Include any user data we have in localStorage
        userId: localStorageData.attributes?.userId || "",
        userEmail: localStorageData.attributes?.userEmail || "",
        firstName: localStorageData.attributes?.firstName || "",
        lastName: localStorageData.attributes?.lastName || "",
        orgName: localStorageData.attributes?.orgName || ""
      }
    };
    
    console.log("Sending sales tracking event:", payload);
    
    // Make the API call
    const response: AxiosResponse<ServerResponse<WebBrowserClientSalesTrackerResDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<WebBrowserClientSalesTrackerResDTO, Error>,
        WebBrowserClientSalesTrackerReqDTO
      >({
        apiUrl: backendApiBasePathLocal + "telemetry/client-tracker/sales-tracker",
        object: payload,
        headers: { "Content-Type": "application/json" },
      });
    
    console.log("Sales tracking event sent successfully:", response);
    
    // Extract and return the tracking ID
    if (
      response &&
      response.data &&
      response.data.data &&      
      response.data.data.trackingId
    ) {
      // Store the last tracking ID in case it's needed later
      
      return response.data.data.trackingId;
    }
    
    return null;
  } catch (error) {
    console.error("Failed to send sales tracking event:", error);
    return null;
  }


};


const SalesTrackerHotIntakeAPICall = async (
  salesTrackerCall: SalesTrackerHotIntakeReqDTO
): Promise<SalesTrackerHotIntakeReqDTO | null> => {
  try {
    const postCallInput: RemotePOSTCallInputObject<SalesTrackerHotIntakeReqDTO> = {
      apiUrl: `${backendApiBasePathLocal}telemetry/client-tracker/sales-tracker/hot-intake?useCase=AGENCY`,
      headers: {},
      object: salesTrackerCall,
    };

    const salesTrackerResp: AxiosResponse<ServerResponse<SalesTrackerHotIntakeReqDTO, Error>> =
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<SalesTrackerHotIntakeReqDTO, Error>,
        SalesTrackerHotIntakeReqDTO
      >(postCallInput);

    if (salesTrackerResp?.data?.errorDetails) {
      console.error(
        'SalesTrackerHotIntakeAPICall rejected:',
        salesTrackerResp.data.errorDetails.message
      );
      return null;
    }

    if (salesTrackerResp?.data?.data) {
      return salesTrackerResp.data.data;
    }

    return null;
  } catch (error: unknown) {
    console.error('SalesTrackerHotIntakeAPICall failed:', error);
    return null;
  }
};


/**
 * Fetches IP address details from the ipinfo.io API.
 * @returns IPAddressDetailsDTO object if successful, null otherwise.
 */
const FetchIPAddressDetails = async (): Promise<IPAddressDetailsDTO | null> => {
  try {
    // Check if we're in Firefox private browsing mode
    const isFirefoxPrivateMode = () => {
      try {
        // First check if we're in Firefox
        const isFirefox = navigator.userAgent.indexOf("Firefox") > -1;
        if (!isFirefox) return false;

        // Test 1: Check localStorage behavior
        const testKey = "firefox_private_mode_test";
        localStorage.setItem(testKey, "1");
        const storedValue = localStorage.getItem(testKey);
        localStorage.removeItem(testKey);
        
        // Test 2: Check IndexedDB availability
        const hasIndexedDB = !!window.indexedDB;
        
        // Test 3: Check if we can create a temporary file system
        const hasFileSystem = !!(window as any).requestFileSystem || !!(window as any).webkitRequestFileSystem;
        
        // Test 4: Check if we can create a temporary database
        const hasDatabase = !!(window as any).openDatabase;
        
        // Test 5: Check if we can create a temporary cache
        const hasCache = !!window.caches;
        
        // In Firefox private mode:
        // - localStorage is available but doesn't persist
        // - IndexedDB is not available
        // - FileSystem API is not available
        // - WebSQL is not available
        // - Cache API is not available
        return isFirefox && 
               (!hasIndexedDB || !hasFileSystem || !hasDatabase || !hasCache) &&
               storedValue === "1"; // Value was stored but will be cleared on exit
      } catch (e) {
        // If any of these checks fail, we're likely in private mode
        return true;
      }
    };

    if(isBotOrCrawler()){      
      return null;
    }
    
    // Don't make the call if in Firefox private browsing
    if (isFirefoxPrivateMode()) {
      // console.log("Skipping IP address fetch in Firefox private browsing mode");
      return null;
    }
    
    // Create a promise that rejects after 350ms
    const timeoutPromise = new Promise<never>((_, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('IP Address fetch timed out after 550ms'));
      }, 550);
    });
    
    // Create the actual fetch promise
    const fetchPromise = remoteCallObject.remoteGETCallV2<IPAddressDetailsDTO>({
      apiUrl: "https://ipinfo.io/json",
      queryParams: { token: "d2f4609d337f64" },
      headers: { "Content-Type": "application/json" },
    });
    
    // Race between the timeout and the fetch
    const response: AxiosResponse<IPAddressDetailsDTO> = await Promise.race([
      fetchPromise,
      timeoutPromise
    ]);

    if (response && response.data) {
      console.log("IP Address details fetched successfully:", response.data);
      return response.data;
    }
    return null;
  } catch (error: any) {
    // Handle 403 Forbidden from ipinfo.io gracefully (rate limit, invalid token, etc.)
    if (error?.response?.status === 403 || error?.originalError?.response?.status === 403) {
      console.log("IP address fetch returned 403 Forbidden (likely rate limit or invalid token), continuing without IP details");
      return null;
    }
    
    // Handle suppressed errors from external APIs
    if (error?.isSuppressed) {
      console.log("External API error suppressed, continuing without IP details");
      return null;
    }
    
    // Handle timeout errors
    if (error?.message?.includes('timed out')) {
      console.log("IP address fetch timed out, continuing without IP details");
      return null;
    }
    
    console.error("Failed to fetch IP address details:", error);
    return null;
  }
};


const UpdateSalesTrackerExitAPICall = async (salesTrackerCall: SalesTrackerExitReqDTO): Promise<WebBrowserClientSalesTrackerResDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<SalesTrackerExitReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}telemetry/client-tracker/sales-tracker/exit-timing`,
    headers: {},
    object: salesTrackerCall,
  };

  const salesTrackerResp: AxiosResponse<ServerResponse<WebBrowserClientSalesTrackerResDTO, Error>> = await remoteCallObject.remotePOSTCallV2<
    ServerResponse<WebBrowserClientSalesTrackerResDTO, Error>,
    SalesTrackerExitReqDTO
  >(postCallInput);

  if (salesTrackerResp && salesTrackerResp.data && salesTrackerResp.data.data) {
    return salesTrackerResp.data.data;
  } else {
    return null;
  }
};



export { SalesTrackerHotIntakeAPICall, TrackSalesEvent, FetchIPAddressDetails, UpdateSalesTrackerExitAPICall };